// The land AROUND a hex field (Synty kit): a ground sheet so the field never ends at open sky, bamboo forest and trees on the inner rings,
// rolling mounds all round, and tall hills / cliffs + a distant Fuji on the far side (away from the camera, so they frame the field without
// blocking the view). Deterministic per tile. Used by the player's own field (paddy.js) and by a visited kingdom (kingdomView.js).
import * as THREE from 'three'
import { FIELD_RADIUS, HEX_SIZE, GROUND_Y, axialToWorld, key, hexDistance, ringCoords, hash01 } from './hexGrid.js'
import { hasRecipe, buildGroundCover, buildScenery, SCENERY_K } from './synty.js'
import { WeedsRecipe } from '../config/balance.js'
import { CEMETERY_KEY } from './cemetery.js'

export function buildSyntySurroundings() {
  const group = new THREE.Group()
  group.name = 'synty-surroundings'
  const k = SCENERY_K()
  const sheet = new THREE.Mesh(new THREE.PlaneGeometry(220, 220), new THREE.MeshLambertMaterial({ color: 0x4f6e34 }))
  sheet.rotation.x = -Math.PI / 2
  sheet.position.y = GROUND_Y - 0.03
  sheet.raycast = () => {} // never block click-to-move
  group.add(sheet)

  const forest = [], items = []
  const FAR = (x, z) => -(x + z) > 0 // camera sits at +x,+z; the far half is behind the field
  for (const { q, r } of ringCoords(FIELD_RADIUS + 1, FIELD_RADIUS + 7)) {
    const d = hexDistance({ q, r }, { q: 0, r: 0 })
    const tier = d - FIELD_RADIUS // 1 (adjacent) … 7
    const kk = key(q, r)
    if (kk === CEMETERY_KEY) continue // the cemetery's plot stays clear (scene/cemetery.js)
    const { x, z } = axialToWorld(q, r)
    const far = FAR(x, z)
    if (tier <= 3) forest.push({ key: kk, x, y: GROUND_Y, z })
    const jx = (hash01(kk + '#sx') - 0.5) * HEX_SIZE, jz = (hash01(kk + '#sz') - 0.5) * HEX_SIZE
    const ry = hash01(kk + '#sr') * Math.PI * 2
    const roll = hash01(kk + '#sp')
    if (tier >= 2 && (far ? roll < 0.75 : roll < 0.35)) {
      // Rolling land on every side; tall hills/cliffs only on the far side and farther out.
      let p = 'Env_GroundMound', sx = k * 0.55, sy = k * 0.9
      if (far && tier >= 3) { p = roll < 0.45 ? 'Env_Hill_Cascade' : 'Env_GroundMound'; sx = k * (0.5 + 0.1 * tier); sy = k * (0.7 + 0.28 * tier) }
      if (far && tier >= 5 && roll > 0.6) { p = 'Env_Cliff'; sx = k * 0.5; sy = k * (0.7 + 0.1 * tier) }
      items.push({ p, x: x + jx, y: GROUND_Y - 0.15, z: z + jz, ry, sx, sy })
    }
    if (tier <= 5 && hash01(kk + '#st') < 0.45) {
      items.push({ p: 'Env_Tree', x: x - jx, y: GROUND_Y, z: z - jz, ry, sx: k * (0.5 + hash01(kk + '#ss') * 0.35) })
    }
    if (hash01(kk + '#sk') < 0.25) {
      items.push({ p: 'Env_Rock', x: x + jz, y: GROUND_Y, z: z + jx, ry, sx: k * (0.9 + hash01(kk + '#sk2') * 1.2) })
    }
  }
  const farD = HEX_SIZE * 1.73 * (FIELD_RADIUS + 9) // a distant Fuji on the far horizon
  items.push({ p: 'Env_Fuji', x: -farD * 0.75, y: GROUND_Y - 0.5, z: -farD * 0.75, ry: 0.4, sx: k * 1.3, sy: k * 1.3 })

  if (forest.length && hasRecipe(WeedsRecipe)) { const f = buildGroundCover(forest, WeedsRecipe); if (f) group.add(f) }
  const scenery = buildScenery(items)
  if (scenery) group.add(scenery)
  return group
}
